/**
 * PROGRESSIVE PLANS LOADER - EVOLV CONTROLLED
 * 
 * Custom loader with animated steps for progressive plans flow.
 * Feature flag controlled via Evolv experimentation platform.
 * 
 * Evolv Control:
 * - window.evolvEnableProgressivePlansLoader = true/false
 * - Allows A/B testing and experimentation
 * - Default: false (disabled until Evolv enables it)
 * 
 * Features:
 * - Custom red loader (#F50A23) with animated steps
 * - Step transitions at 2s and 4s while the loader is visible
 * - VDS icons (home-internet, wifi-wireless, plan-speed-ultimate) via React components
 * 
 * Usage:
 * - showLoader(null, true)  // Enable progressive plans loader
 * - showLoader(null, false) // Use default loader
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { ProgressivePlansLoader } from './ProgressivePlansLoader';
import { eventDispatcher } from '../Tagging';

let setIntervalId;
let messageRotationIntervalId;
let progressivePlansLoaderStartTime = null;

const LOADER_MESSAGE_INTERVAL = 3000;
const progressivePlansLoaderRoots = new WeakMap();

const getLoaderTextElement = () => document.getElementById('loader-animation-text');

const removeLoaderTextElement = () => {
  const textElement = getLoaderTextElement();
  if (textElement) {
    textElement.remove();
  }
};

const renderProgressivePlansLoader = (container) => {
  if (!progressivePlansLoaderRoots.has(container)) {
    progressivePlansLoaderRoots.set(container, createRoot(container));
  }
  progressivePlansLoaderRoots.get(container).render(<ProgressivePlansLoader />);
};

const removeProgressivePlansLoader = (container) => {
  const root = progressivePlansLoaderRoots.get(container);
  if (root) {
    root.unmount();
    progressivePlansLoaderRoots.delete(container);
  }
  container?.remove();
};

export const hideLoader = () => {
  hideLoaderNow();
};

const hideLoaderNow = () => {
  clearInterval(setIntervalId);
  clearInterval(messageRotationIntervalId);
  
  // Clean up loader message element
  removeLoaderTextElement();
  
  const ele = document.querySelectorAll('#loader');
  if (ele && ele.length > 0) {
    ele.forEach((item) => {
      const loader = item;
      
      // Track progressive plans loader duration before hiding
      if (loader.classList.contains('progressive-plans-active') && progressivePlansLoaderStartTime) {
        const endTime = Date.now();
        const durationInSeconds = Math.round((endTime - progressivePlansLoaderStartTime) / 1000);
        
        // Fire analytics for loader hide with duration
        
          eventDispatcher('sendData', {
            name: '',
            svars: {
              evar13: `pp_loader_${durationInSeconds} sec`,
            },
          });
        
        // Reset the start time
        progressivePlansLoaderStartTime = null;
      }
      
      loader.style.display = 'none';
      
      // Clean up progressive plans loader state
      loader.classList.remove('progressive-plans-active');
      
      // Reset loader spinner to default
      const loaderSpinner = loader.querySelector('.loader');
      if (loaderSpinner) {
        loaderSpinner.className = 'loader';
      }
      
      // Unmount React component and remove steps wrapper if it exists
      const stepsWrapper = loader.querySelector('#progressive-plans-steps-wrapper');
      if (stepsWrapper) {
        removeProgressivePlansLoader(stepsWrapper);
      }
    });
  }
};

const configureProgressivePlansStyles = (loaderElement, useProgressivePlans) => {
  if (useProgressivePlans) {
    loaderElement.classList.add('progressive-plans-active');
  } else {
    loaderElement.classList.remove('progressive-plans-active');
  }
};

const updateLoaderSpinner = (loaderSpinner, useProgressivePlans) => {
  const newClassName = useProgressivePlans ? 'loader loader-progressive-plans' : 'loader';
  loaderSpinner.setAttribute('class', newClassName);
};

const manageProgressivePlansSteps = (loaderElement, loaderSpinner, useProgressivePlans) => {
  const stepsWrapper = loaderElement.querySelector('#progressive-plans-steps-wrapper');
  
  if (useProgressivePlans) {
    if (!stepsWrapper) {
      const newStepsWrapper = document.createElement('div');
      newStepsWrapper.setAttribute('id', 'progressive-plans-steps-wrapper');
      loaderSpinner.insertAdjacentElement('afterend', newStepsWrapper);
      renderProgressivePlansLoader(newStepsWrapper);
    }
  } else if (stepsWrapper) {
    removeProgressivePlansLoader(stepsWrapper);
  }
};

const updateExistingLoader = (loaderElement, useProgressivePlans) => {
  configureProgressivePlansStyles(loaderElement, useProgressivePlans);
  
  const loaderSpinner = loaderElement.querySelector('.loader');
  if (loaderSpinner) {
    updateLoaderSpinner(loaderSpinner, useProgressivePlans);
    manageProgressivePlansSteps(loaderElement, loaderSpinner, useProgressivePlans);
  }
  
  loaderElement.setAttribute('style', 'display: block;');
};

const createLoaderElement = (useProgressivePlans) => {
  const injectLoader = document.createElement('div');
  injectLoader.setAttribute('id', 'loader');
  injectLoader.setAttribute('aria-label', 'loader overlay');
  
  const overlayClass = useProgressivePlans ? 'loaderOverlay progressive-plans-active' : 'loaderOverlay';
  injectLoader.setAttribute('class', overlayClass);
  injectLoader.setAttribute('role', 'alert');
  injectLoader.setAttribute('aria-live', 'assertive');
  
  const loaderSpinner = document.createElement('div');
  loaderSpinner.className = useProgressivePlans ? 'loader loader-progressive-plans' : 'loader';
  injectLoader.appendChild(loaderSpinner);
  
  if (useProgressivePlans) {
    const stepsWrapper = document.createElement('div');
    stepsWrapper.setAttribute('id', 'progressive-plans-steps-wrapper');
    injectLoader.appendChild(stepsWrapper);
    renderProgressivePlansLoader(stepsWrapper);
  }
  
  const contentLoading = document.createElement('div');
  contentLoading.className = 'content_update_loading';
  injectLoader.appendChild(contentLoading);
  
  return injectLoader;
};

const setContentLoadingLabel = () => {
  const loaderContent = document.querySelector('.content_update_loading');
  if (loaderContent) {
    loaderContent.ariaLabel = 'content is loading';
  }
};

export const displayLoaderMessage = (message) => {
  if (!message) {
    removeLoaderTextElement();
    return;
  }
  
  const messages = Array.isArray(message) ? message : [message];
  
  if (messages && messages.length > 0) {
    const loaderElement = document.getElementById('loader');
    let textElement = getLoaderTextElement();
    
    if (loaderElement && !textElement) {
      textElement = document.createElement('div');
      textElement.setAttribute('id', 'loader-animation-text');
      textElement.setAttribute('role', 'alert');
      textElement.setAttribute('aria-live', 'assertive');
      textElement.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); margin-top: -3rem; text-align: center; font-weight: bold; z-index: 4; font-family: "VerizonNHGDS", "Arial", "Helvetica Neue", Helvetica, Arial, sans-serif;';
      loaderElement.appendChild(textElement);
    }
    
    if (!textElement) {
      return;
    }
    
    let index = 0;
    textElement.innerHTML = messages[index];
    index += 1;
    
    if (messageRotationIntervalId) {
      clearInterval(messageRotationIntervalId);
    }
    
    messageRotationIntervalId = setInterval(() => {
      const currentTextElement = getLoaderTextElement();
      if (!currentTextElement) {
        clearInterval(messageRotationIntervalId);
        return;
      }
      
      if (index < messages.length) {
        currentTextElement.innerHTML = messages[index];
        index += 1;
      } else {
        clearInterval(messageRotationIntervalId);
      }
    }, LOADER_MESSAGE_INTERVAL);
  }
};

export const showLoader = (message, useProgressivePlansLoader = false) => {
  try {
    const ele = document.getElementById('loader');
    clearInterval(setIntervalId);
    clearInterval(messageRotationIntervalId);
    
    if (ele) {
      const isAlreadyVisible = ele.style.display === 'block';
      const wasProgressivePlansActive = ele.classList.contains('progressive-plans-active');
      
      // Always update loader configuration to handle progressive plans loader switch
      updateExistingLoader(ele, useProgressivePlansLoader);
      
      // Track progressive plans loader start only when it's newly activated
      if (useProgressivePlansLoader && !wasProgressivePlansActive) {
        progressivePlansLoaderStartTime = Date.now();
        
        // Fire analytics for loader start
          eventDispatcher('sendData', {
            name: '',
            svars: {
              evar13: 'pp_loader_start',
            },
          });
        
      }
      
      if (isAlreadyVisible) {
        setContentLoadingLabel();
        // Performance improve and avoids calling focusTrap.activate() twice;
        return;
      }
    } else {
      const injectLoader = createLoaderElement(useProgressivePlansLoader);
      document.body.appendChild(injectLoader);
      setContentLoadingLabel();
      
      // Track progressive plans loader start for new loader
      if (useProgressivePlansLoader) {
        progressivePlansLoaderStartTime = Date.now();
        
        // Fire analytics for loader start
        try {
          eventDispatcher('sendData', {
            name: '',
            svars: {
              evar13: 'pp_loader_start',
            },
          });
        } catch (error) {
          console.log('Error firing loader start analytics:', error);
        }
      }
    }
    
    displayLoaderMessage(message);
    
    setIntervalId = setInterval(() => {
      const loaderContent = document.querySelector('.content_update_loading');
      if (loaderContent) {
        loaderContent.innerHTML = '';
        setTimeout(() => {
          loaderContent.ariaLabel = 'still loading';
        }, 1000);
      } else {
        clearInterval(setIntervalId);
      }
    }, 60000);
  } catch (e) {
    console.log(e);
  }
};

export const hideHTMLLoader = () => {
  clearInterval(setIntervalId);
  clearInterval(messageRotationIntervalId);
  
  // Clean up loader message element
  removeLoaderTextElement();
  
  const ele = document.getElementById('loader');
  if (ele) {
    ele.style.display = 'none';
  }
};
